// API utility functions for Jupiter Institute

// Override with REACT_APP_API_URL / REACT_APP_STORAGE_URL in a .env file for other environments.
export const API_BASE_URL = (process.env.REACT_APP_API_URL || "https://api.jupiterinstitution.com/api").replace(/\/$/, "");
export const STORAGE_BASE_URL = (
  process.env.REACT_APP_STORAGE_URL || API_BASE_URL.replace(/\/api$/, "") + "/storage"
).replace(/\/$/, "");

// Build a full URL for a file stored on the backend (question images, etc.)
export const storageUrl = (path) => {
  if (!path) return null;
  if (/^(https?:|data:|blob:)/.test(path)) return path;
  return `${STORAGE_BASE_URL}/${String(path).replace(/^\/+/, "")}`;
};

// Get auth token from localStorage
export const getAuthToken = () => {
  return localStorage.getItem("authToken");
};

// Get user data from localStorage
export const getUserData = () => {
  const userData = localStorage.getItem("userData");
  return userData ? JSON.parse(userData) : null;
};

// Check if user is authenticated
export const isAuthenticated = () => {
  const token = getAuthToken();
  const userData = getUserData();
  return !!(token && userData);
};

// Clear authentication data
export const clearAuth = () => {
  localStorage.removeItem("authToken");
  localStorage.removeItem("userData");
};

const buildHeaders = (options = {}) => {
  const token = getAuthToken();
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  return {
    Accept: "application/json",
    // Let the browser set the multipart boundary for FormData bodies
    ...(!isFormData && { "Content-Type": "application/json" }),
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };
};

// Raw authenticated fetch — use when the caller needs the Response object (status, ok)
export const authFetch = (endpoint, options = {}) =>
  fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers: buildHeaders(options) });

const handleSessionExpired = () => {
  clearAuth();
  window.location.href = "/login";
};

// Calls made while signed out: a 401 from these is a wrong password, not an expired session
const PUBLIC_ENDPOINTS = ["/login", "/forgot-password", "/reset-password"];

const NO_ADMIN_ACCESS = "You don't have admin access for this action.";

// API request wrapper with authentication
export const apiRequest = async (endpoint, options = {}) => {
  try {
    const response = await authFetch(endpoint, options);

    let data;
    try {
      data = await response.json();
    } catch {
      data = {
        success: false,
        message: `Server returned an unexpected response (HTTP ${response.status}).`,
      };
    }

    // 401 = expired, invalid or revoked token (or a blocked account): clear auth and go to login.
    // 403 = signed in but not allowed: keep the session and show the message.
    if (!PUBLIC_ENDPOINTS.includes(endpoint) && response.status === 401) {
      handleSessionExpired();
      return null;
    }
    if (response.status === 403 && !PUBLIC_ENDPOINTS.includes(endpoint)) {
      return { ...data, success: false, message: data?.message || NO_ADMIN_ACCESS };
    }
    if (response.status === 429) {
      return { ...data, success: false, message: data?.message || "Too many requests. Wait a minute and try again." };
    }

    // The status travels with the body so pages can tell 409 (in use) and 422 (invalid) apart
    return data && typeof data === "object" ? { ...data, status: response.status } : data;
  } catch (error) {
    console.error("API request failed:", error);
    throw error;
  }
};

// First validation message of a 422 response, or its message
export const firstError = (data, fallback = "Something went wrong.") => {
  const errors = data?.errors && Object.values(data.errors).flat();
  return (errors && errors[0]) || data?.message || fallback;
};

const query = (params = {}) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "" && v !== "all")
  ).toString();
  return qs ? `?${qs}` : "";
};

// Login API call
export const loginAPI = async (email, password) => {
  return await apiRequest("/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
};

// Revoke the token on the server. Failures are ignored: signing out always clears the browser.
export const logoutAPI = async () => {
  try {
    await authFetch("/logout", { method: "POST" });
  } catch {
    // offline or already invalid
  }
};

// Email a reset link (the link opens /reset-password in this panel for admins)
export const forgotPasswordAPI = (email) =>
  apiRequest("/forgot-password", { method: "POST", body: JSON.stringify({ email }) });

export const resetPasswordAPI = ({ token, email, password, passwordConfirmation }) =>
  apiRequest("/reset-password", {
    method: "POST",
    body: JSON.stringify({ token, email, password, password_confirmation: passwordConfirmation }),
  });

// Create Test Series API call
export const createTestSeriesAPI = async (testSeriesData) => {
  return await apiRequest("/test-series", {
    method: "POST",
    body: JSON.stringify(testSeriesData),
  });
};

// Fetch Test Series API call
export const fetchTestSeriesAPI = async () => {
  return await apiRequest("/test-series", {
    method: "GET",
  });
};

// Get Test Series by ID API call
export const getTestSeriesByIdAPI = async (id) => {
  return await apiRequest(`/test-series/${id}`, {
    method: "GET",
  });
};

// Update Test Series API call
export const updateTestSeriesAPI = async (id, testSeriesData) => {
  return await apiRequest(`/test-series/${id}`, {
    method: "PUT",
    body: JSON.stringify(testSeriesData),
  });
};

// Delete Test Series API call
export const deleteTestSeriesAPI = async (id) => {
  return await apiRequest(`/test-series/${id}`, {
    method: "DELETE",
  });
};

// Copy a test with its sections and questions (the copy is a draft)
export const duplicateTestSeriesAPI = (id) => apiRequest(`/test-series/${id}/duplicate`, { method: "POST" });

// Fetch Leaderboard by Test Series ID API call
export const fetchLeaderboardAPI = async (testSeriesId) => {
  return await apiRequest(`/test-series-admin/${testSeriesId}/leaderboard`, {
    method: "GET",
  });
};

// Fetch Test Attempts History by Test Series ID and User ID API call
export const fetchTestAttemptsHistoryAPI = async (testSeriesId, userId) => {
  return await apiRequest(`/admin/test-attempts-history/${testSeriesId}/${userId}`, {
    method: "GET",
  });
};

// Fetch Review Answers by Attempt ID and User ID API call
export const fetchReviewAnswersAPI = async (attemptId, userId) => {
  return await apiRequest(`/admin/test-attempts/${attemptId}/answers/${userId}`, {
    method: "GET",
  });
};

// Correct / incorrect / unanswered counts per question over every submitted attempt
export const fetchQuestionStatsAPI = (testSeriesId) => apiRequest(`/test-series-admin/${testSeriesId}/question-stats`);

export const exportLeaderboardCsv = (testSeriesId) =>
  downloadCsv(`/test-series-admin/${testSeriesId}/leaderboard/export`, "leaderboard.csv");

// ===================== SECTION API CALLS =====================

// Fetch Sections by Test Series ID API call
export const fetchSectionsAPI = async (testSeriesId) => {
  return await apiRequest(`/sections/${testSeriesId}`, {
    method: "GET",
  });
};

// Create Section API call
export const createSectionAPI = async (sectionData) => {
  return await apiRequest("/sections", {
    method: "POST",
    body: JSON.stringify(sectionData),
  });
};

// Update Section API call
export const updateSectionAPI = async (id, sectionData) => {
  return await apiRequest(`/sections/${id}`, {
    method: "PUT",
    body: JSON.stringify(sectionData),
  });
};

// Delete Section API call
export const deleteSectionAPI = async (id) => {
  return await apiRequest(`/sections/${id}`, {
    method: "DELETE",
  });
};

// Save the order of a test's sections (ids in the new order)
export const reorderSectionsAPI = (testSeriesId, ids) =>
  apiRequest("/sections/reorder", { method: "PUT", body: JSON.stringify({ test_series_id: testSeriesId, ids }) });

// One section with its questions (data is a one-item array)
export const fetchSectionWithQuestionsAPI = async (sectionId) => {
  const response = await apiRequest(`/fetch-section/${sectionId}`);
  if (!response) return response;
  const data = Array.isArray(response.data) ? response.data[0] : response.data;
  return { ...response, data: data || null };
};

// ===================== QUESTION API CALLS =====================

// Submit Question API call
export const submitQuestionAPI = async (questionData) => {
  return await apiRequest("/questions", {
    method: "POST",
    body: questionData, // FormData object — Content-Type is set by the browser
  });
};

// Save the order of a section's questions (ids in the new order)
export const reorderQuestionsAPI = (sectionId, ids) =>
  apiRequest("/questions/reorder", { method: "PUT", body: JSON.stringify({ section_id: sectionId, ids }) });

// Move a question to another section, or copy it there
export const moveQuestionAPI = (questionId, sectionId, copy = false) =>
  apiRequest(`/questions/${questionId}/move`, { method: "POST", body: JSON.stringify({ section_id: sectionId, copy }) });

export const deleteQuestionAPI = (questionId) => apiRequest(`/questions/${questionId}`, { method: "DELETE" });

// ===================== COURSE API CALLS =====================

// Fetch All Courses API call
export const fetchCoursesAPI = async () => {
  return await apiRequest("/courses", {
    method: "GET",
  });
};

// Get Course by ID API call
export const getCourseByIdAPI = async (id) => {
  return await apiRequest(`/courses/${id}`, {
    method: "GET",
  });
};

// Create Course API call
export const createCourseAPI = async (courseData) => {
  return await apiRequest("/courses", {
    method: "POST",
    body: JSON.stringify(courseData),
  });
};

// Update Course API call
export const updateCourseAPI = async (id, courseData) => {
  return await apiRequest(`/courses/${id}`, {
    method: "PUT",
    body: JSON.stringify(courseData),
  });
};

// Delete Course API call
export const deleteCourseAPI = async (id) => {
  return await apiRequest(`/courses/${id}`, {
    method: "DELETE",
  });
};

// ===================== COURSE PLANS API CALLS =====================

// Fetch Plans by Course ID API call (no bearer token)
export const fetchPlansByCourseAPI = async (courseId) => {
  const config = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      // No authorization header for GET requests as per requirements
    },
  };

  try {
    const response = await fetch(
      `${API_BASE_URL}/courses/${courseId}/plans`,
      config
    );
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("API request failed:", error);
    throw error;
  }
};

// Get Plan by ID API call (no bearer token)
export const getPlanByIdAPI = async (planId) => {
  const config = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      // No authorization header for GET requests as per requirements
    },
  };

  try {
    const response = await fetch(`${API_BASE_URL}/plans/${planId}`, config);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("API request failed:", error);
    throw error;
  }
};

// Create Plan API call (with bearer token)
export const createPlanAPI = async (courseId, planData) => {
  return await apiRequest(`/courses/${courseId}/plans`, {
    method: "POST",
    body: JSON.stringify(planData),
  });
};

// Update Plan API call (with bearer token)
export const updatePlanAPI = async (planId, planData) => {
  return await apiRequest(`/plans/${planId}`, {
    method: "PUT",
    body: JSON.stringify(planData),
  });
};

// Delete Plan API call (with bearer token)
export const deletePlanAPI = async (planId) => {
  return await apiRequest(`/plans/${planId}`, {
    method: "DELETE",
  });
};

// Change the signed-in admin's own password (the server takes the user from the token)
export const changeAdminPasswordAPI = async (passwordData) => {
  return await apiRequest("/change-admin-password", {
    method: "POST",
    body: JSON.stringify({
      old_password: passwordData.oldPassword,
      new_password: passwordData.newPassword,
      confirm_password: passwordData.confirmPassword,
    }),
  });
};

// ===================== USERS =====================

// params: search, page, per_page, role (admin|user), subscribed (1|0), status (active|blocked)
export const fetchUsersAPI = (params) => apiRequest(`/users${query(params)}`);

// changes: { status: "active"|"blocked" } or { role: "admin"|"user" }
export const updateUserAPI = (id, changes) =>
  apiRequest(`/users/${id}`, { method: "PATCH", body: JSON.stringify(changes) });

// Without a password the server generates a temporary one and returns it once
export const resetUserPasswordAPI = (id, password) =>
  apiRequest(`/users/${id}/reset-password`, {
    method: "POST",
    body: JSON.stringify(password ? { password, password_confirmation: password } : {}),
  });

export const exportUsersCsv = (params) => downloadCsv(`/users/export${query(params)}`, "users.csv");

// ===================== SUBSCRIPTIONS =====================

// params: page, per_page, search, status (active|expired|pending|failed|cancelled), from, to (Y-m-d)
export const fetchSubscriptionsAPI = (params) => apiRequest(`/fetch-subscriptions${query(params)}`);

export const exportSubscriptionsCsv = (params) =>
  downloadCsv(`/fetch-subscriptions/export${query(params)}`, "subscriptions.csv");

// { user_id, course_plan_id, starts_at?, months?, amount?, note? }
export const grantSubscriptionAPI = (payload) =>
  apiRequest("/subscriptions", { method: "POST", body: JSON.stringify(payload) });

// { action: "extend", months?, days? } | { action: "cancel", note? } | { action: "reactivate" }
export const updateSubscriptionAPI = (id, payload) =>
  apiRequest(`/subscriptions/${id}`, { method: "PATCH", body: JSON.stringify(payload) });

// Fetch Dashboard Statistics API call
export const fetchDashboardStatsAPI = async () => {
  return await apiRequest("/dashboard-stats", {
    method: "GET",
  });
};

// ===================== QUESTION IMPORT / EXPORT =====================

// Like apiRequest, but also returns the HTTP status (commit/undo need 404/409)
const requestWithStatus = async (endpoint, options = {}) => {
  const response = await authFetch(endpoint, options);
  let data;
  try {
    data = await response.json();
  } catch {
    data = { success: false, message: `Server returned an unexpected response (HTTP ${response.status}).` };
  }
  if (response.status === 401) {
    handleSessionExpired();
    return { status: 401, data: null };
  }
  if (response.status === 403) {
    data = { ...data, success: false, message: data?.message || NO_ADMIN_ACCESS };
  }
  if (response.status === 429) {
    data = { success: false, message: "Too many uploads in a short time. Wait a minute and try again." };
  }
  return { status: response.status, data };
};

// Save a Blob as a file in the browser's downloads
export const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking synchronously can cancel the download in Safari/Firefox
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Download a CSV from an authenticated endpoint (a plain <a href> cannot send the Bearer token)
export const downloadCsv = async (endpoint, fallbackName) => {
  const response = await authFetch(endpoint, { method: "GET", headers: { Accept: "text/csv" } });
  if (response.status === 401) {
    handleSessionExpired();
    return;
  }
  if (!response.ok) {
    let message = `Download failed (HTTP ${response.status}).`;
    try {
      message = (await response.json()).message || message;
    } catch {}
    throw new Error(message);
  }
  const disposition = response.headers.get("Content-Disposition") || "";
  const name = /filename="?([^";]+)"?/i.exec(disposition)?.[1] || fallbackName;
  saveBlob(await response.blob(), name);
};

// withSections: section columns first plus a Physics/Chemistry/Mathematics example (series scope only).
// subjects: the template for a test split into subjects (subject, subject_description, section, …).
export const downloadImportTemplate = (type = "ALL", scope = "section", withSections = false, subjects = false) =>
  withSections
    ? downloadCsv(
        `/questions/import/template?type=${type}&scope=series&with_sections=1${subjects ? "&layout=subjects" : ""}`,
        subjects ? "subjects_sections_questions_template.csv" : "sections_questions_template.csv"
      )
    : downloadCsv(`/questions/import/template?type=${type}&scope=${scope}`, `questions_template_${type}.csv`);

// withSections (series only): include section settings so the file can be re-imported with sections
export const exportQuestionsCsv = ({ sectionId, testSeriesId, withSections = false }) =>
  downloadCsv(
    sectionId
      ? `/questions/export?section_id=${sectionId}`
      : `/questions/export?test_series_id=${testSeriesId}${withSections ? "&with_sections=1" : ""}`,
    "questions.csv"
  );

// { status, data } — 200 even when rows have errors; 422 for file-level problems
export const previewQuestionImport = ({
  file,
  images,
  sectionId,
  testSeriesId,
  mode,
  skipInvalid,
  createSections = false,
  updateSections = false,
}) => {
  const body = new FormData();
  body.append("file", file);
  if (images) body.append("images", images);
  if (sectionId) {
    body.append("section_id", sectionId);
  } else {
    body.append("test_series_id", testSeriesId);
    // Section creation/updates exist only for a series import; the server rejects create_sections with section_id
    body.append("create_sections", createSections ? "1" : "0");
    body.append("update_sections", updateSections ? "1" : "0");
  }
  body.append("mode", mode);
  body.append("skip_invalid", skipInvalid ? "1" : "0");
  return requestWithStatus("/questions/import/preview", { method: "POST", body });
};

export const commitQuestionImport = (importId) =>
  requestWithStatus(`/questions/import/${importId}/commit`, { method: "POST" });

export const fetchImportBatches = ({ sectionId, testSeriesId }) =>
  apiRequest(
    sectionId
      ? `/questions/import-batches?section_id=${sectionId}`
      : `/questions/import-batches?test_series_id=${testSeriesId}`
  );

export const undoImportBatch = (batchId) =>
  requestWithStatus(`/questions/import-batches/${batchId}`, { method: "DELETE" });
